import type { QueryClient } from '@tanstack/vue-query'
import { computed, ref, toValue, watch, type ComputedRef, type MaybeRefOrGetter, type WatchStopHandle } from 'vue'
import { useAuthStore } from '../stores/auth.store'

type AuthStore = ReturnType<typeof useAuthStore>

/** One binding per application/SSR request; never a process-global query client. */
export function bindAuthQueryClient(queryClient: QueryClient, auth: AuthStore): WatchStopHandle {
  return watch(
    () => [auth.user?.id ?? null, auth.user?.role ?? null],
    (current, previous) => {
      if (current[0] === previous[0] && current[1] === previous[1]) return
      // Cancellation prevents late responses from filling a cache after logout.
      // Clear mutations as well: their cached variables can contain private data.
      void queryClient.cancelQueries()
      queryClient.clear()
    },
    { flush: 'sync' },
  )
}

/** Append scope so existing feature-prefix invalidation keeps working. */
export function useAuthQueryKey(key: () => readonly unknown[]): ComputedRef<readonly unknown[]> {
  const auth = useAuthStore()
  return computed(() => [...key(), { auth: { userId: auth.user?.id ?? null, role: auth.user?.role ?? null } }])
}

/** Capabilities must partition cached data without storing the secret in a key. */
export function useCredentialQueryScope(credential: MaybeRefOrGetter<string | null | undefined>): ComputedRef<string> {
  const scope = ref(crypto.randomUUID())
  watch(
    () => toValue(credential),
    () => {
      scope.value = crypto.randomUUID()
    },
    { flush: 'sync' },
  )
  return computed(() => (toValue(credential) ? scope.value : 'account'))
}
